import {useLayoutEffect, useState} from 'react';

/**
 * Truncates `text` to the longest prefix that, when followed by `suffix`,
 * still fits in `desktopLines` lines (or `mobileLines` on viewports
 * narrower than `mobileBreakpoint`px) when rendered inside `targetRef`.
 *
 * Uses a hidden probe div with the computed font metrics of the target
 * and binary-searches the prefix length. Returns the truncated prefix as
 * a string when truncation is needed, or `null` when the full text
 * already fits / is being shown.
 *
 * Re-runs on text change, viewport resize, and when `enabled` flips.
 * Used by the Customer Feedback page (GROW-2236) to keep "View All"
 * inline with the truncated review text.
 *
 * @param {string} text The full text to potentially truncate.
 * @param {React.RefObject<HTMLElement>} targetRef Ref to the rendered
 *   text container — its computed font + clientWidth drive the measurement.
 * @param {object} options
 * @param {string} [options.suffix='... View All'] Reserved-space suffix
 *   appended during measurement (the inline "View All" link).
 * @param {number|string} [options.suffixFontWeight] Font weight to apply
 *   to the suffix during measurement. Use when the rendered suffix has a
 *   heavier weight than the parent text (e.g. the "View All" button is
 *   `font-weight: 700` while the comment is `font-weight: 400`). If
 *   omitted, the suffix is measured at the parent's weight.
 * @param {number} [options.desktopLines=2] Max lines on desktop.
 * @param {number} [options.mobileLines=4] Max lines below mobile breakpoint.
 * @param {number} [options.mobileBreakpoint=767] Mobile media-query cutoff
 *   in pixels.
 * @param {boolean} [options.enabled=true] When false, returns `null` and
 *   skips measurement (e.g. when the consumer is in an expanded state).
 * @returns {string | null} The truncated prefix, or `null` when no
 *   truncation is needed / `enabled` is false.
 */
const useInlineTruncation = (
  text,
  targetRef,
  {
    suffix = '... View All',
    suffixFontWeight,
    desktopLines = 2,
    mobileLines = 4,
    mobileBreakpoint = 767,
    enabled = true
  } = {}
) => {
  const [truncated, setTruncated] = useState(null);

  useLayoutEffect(() => {
    const el = targetRef.current;
    if (!el) return undefined;

    const compute = () => {
      if (!enabled || !text) {
        setTruncated(null);
        return;
      }

      const styles = window.getComputedStyle(el);
      const lineHeight = parseFloat(styles.lineHeight);
      const width = el.clientWidth;
      if (!width || !lineHeight) {
        setTruncated(null);
        return;
      }

      const isMobile = window.matchMedia(`(max-width: ${mobileBreakpoint}px)`)
        .matches;
      const maxLines = isMobile ? mobileLines : desktopLines;
      const maxHeight = lineHeight * maxLines + 1;

      const probe = document.createElement('div');
      probe.style.cssText = [
        'visibility:hidden',
        'position:absolute',
        'top:0',
        'left:-9999px',
        `width:${width}px`,
        `font-family:${styles.fontFamily}`,
        `font-size:${styles.fontSize}`,
        `font-weight:${styles.fontWeight}`,
        `line-height:${styles.lineHeight}`,
        'box-sizing:border-box',
        'word-wrap:break-word',
        'white-space:normal'
      ].join(';');
      document.body.appendChild(probe);

      probe.textContent = text;
      if (probe.scrollHeight <= maxHeight) {
        document.body.removeChild(probe);
        setTruncated(null);
        return;
      }

      const renderProbe = (prefix) => {
        if (suffixFontWeight == null) {
          probe.textContent = `${prefix}${suffix}`;
          return;
        }
        probe.textContent = prefix;
        const suffixNode = document.createElement('span');
        suffixNode.style.fontWeight = String(suffixFontWeight);
        suffixNode.textContent = suffix;
        probe.appendChild(suffixNode);
      };

      let lo = 0;
      let hi = text.length;
      while (lo < hi) {
        const mid = Math.floor((lo + hi + 1) / 2);
        renderProbe(text.slice(0, mid).trimEnd());
        if (probe.scrollHeight <= maxHeight) {
          lo = mid;
        } else {
          hi = mid - 1;
        }
      }

      document.body.removeChild(probe);
      setTruncated(text.slice(0, lo).trimEnd());
    };

    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, [
    text,
    targetRef,
    suffix,
    suffixFontWeight,
    desktopLines,
    mobileLines,
    mobileBreakpoint,
    enabled
  ]);

  return truncated;
};

export default useInlineTruncation;
